import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Module } from "@/constants";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { PayrollsService } from "@/services/payrolls";
import { ProjectDependsOn } from "@/schemas/commonFields";
import { useQueryClient } from "@tanstack/react-query";
import {
  PayrollDepartmentBasedInspectionAssignment,
  PayrollRoleBasedInspectionAssignment,
} from "../types";
import { useAsyncOperationProgress } from "@/components/progress";

interface UseCreatePayrollEntriesFromAssignmentsMutationProps {
  payrollId: string;
  projectDependsOn: ProjectDependsOn | undefined;
  progress: ReturnType<typeof useAsyncOperationProgress>;
}

export const useCreatePayrollEntriesFromAssignmentsMutation = ({
  payrollId,
  projectDependsOn,
  progress,
}: UseCreatePayrollEntriesFromAssignmentsMutationProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const mutationFn = useCallback(
    async (
      selectedAssignments: {
        id: string;
        label: string;
        assignment:
          | PayrollRoleBasedInspectionAssignment
          | PayrollDepartmentBasedInspectionAssignment;
      }[],
    ) => {
      if (projectDependsOn === undefined) {
        return Promise.resolve();
      }

      // Initialize progress tracking
      progress.initialize(
        selectedAssignments.map((item) => ({
          id: item.id,
          label: item.label,
          success: false,
        })),
      );
      progress.startProcessing();

      // Process assignments sequentially to show progress
      try {
        for (const item of selectedAssignments) {
          if (progress.isCancelling()) {
            break;
          }

          progress.setCurrentItem(item.label);

          try {
            await PayrollsService.createPayrollInspections(
              item.assignment,
              projectDependsOn,
            );
            progress.markItemProcessed(item.id, true);
          } catch (error) {
            progress.markItemProcessed(item.id, false);
          }

          if (progress.isCancelling()) {
            break;
          }
        }
      } finally {
        progress.complete();
      }

      return;
    },
    [projectDependsOn],
  );

  return useMutationWithErrorHandling({
    mutationFn,
    errorTitle: t("payrolls.errors.createEntry"),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [Module.Payrolls, payrollId],
      });
    },
  });
};
